import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formRecordCode } from './formBuilderModel'

function fmtWhen(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return String(iso)
  }
}

function matchesFormFilter(form, needle) {
  if (!needle) return true
  const q = needle.toLowerCase()
  const code = formRecordCode(form).toLowerCase()
  return (
    String(form.name || '').toLowerCase().includes(q)
    || String(form.slug || '').toLowerCase().includes(q)
    || code.includes(q)
  )
}

function groupLabel(category) {
  if (category === 'WHS') return 'WHS / workplace safety'
  if (category === 'Staff') return 'Staff & rostering'
  if (category === 'Patron:Compliance') return 'Patron — Compliance & KYC'
  if (category === 'Patron:Gaming') return 'Patron — Gaming'
  if (category === 'Patron:Service') return 'Patron — Service & complaints'
  return 'Other forms'
}

function formGroupKey(f) {
  const seedSlug = f.workingSettings?.seedMeta?.slug || f.slug || ''
  if (seedSlug.startsWith('whs-') || f.slug?.startsWith('whs-')) return 'WHS'
  if (seedSlug.startsWith('staff-') || f.slug?.startsWith('staff-')) return 'Staff'
  if (f.facing === 'patron' || seedSlug.startsWith('patron-') || f.slug?.startsWith('patron-')) {
    const cat = f.workingSettings?.seedMeta?.category || 'Compliance'
    return `Patron:${cat}`
  }
  return 'Other'
}

export default function FormRecords() {
  const { formId } = useParams()
  const navigate = useNavigate()
  const [forms, setForms] = useState([])
  const [form, setForm] = useState(null)
  const [rows, setRows] = useState([])
  const [formFilter, setFormFilter] = useState('')
  const [recordQ, setRecordQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [formsLoading, setFormsLoading] = useState(true)
  const [error, setError] = useState('')
  const [collapsed, setCollapsed] = useState({})

  const loadForms = useCallback(async () => {
    setFormsLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/forms?includeArchived=1'))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load forms')
      const list = (j.data || []).slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      setForms(list)
      return list
    } finally {
      setFormsLoading(false)
    }
  }, [])

  const loadRecords = useCallback(async (id, search) => {
    if (!id) {
      setForm(null)
      setRows([])
      return
    }
    setLoading(true)
    setError('')
    try {
      const qs = search ? `?q=${encodeURIComponent(search)}` : ''
      const res = await authFetch(getLocalApiUrl(`/api/forms/${id}/submissions${qs}`))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load records')
      setForm(j.data?.form || null)
      setRows(j.data?.submissions || [])
    } catch (e) {
      setError(e.message)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadForms()
      .then((list) => {
        if (formId) return
        const first = list.find((f) => (f.submissionCount || 0) > 0) || list[0]
        if (first) navigate(`/compliance-safe/forms/${first.id}/records`, { replace: true })
      })
      .catch((e) => setError(e.message))
  }, [formId, loadForms, navigate])

  useEffect(() => {
    if (formId) loadRecords(formId, recordQ)
  }, [formId, recordQ, loadRecords])

  const filteredForms = useMemo(
    () => forms.filter((f) => matchesFormFilter(f, formFilter.trim())),
    [forms, formFilter]
  )

  const groupedForms = useMemo(() => {
    const groups = new Map()
    for (const f of filteredForms) {
      const cat = formGroupKey(f)
      if (!groups.has(cat)) groups.set(cat, [])
      groups.get(cat).push(f)
    }
    const order = ['WHS', 'Staff', 'Patron:Compliance', 'Patron:Gaming', 'Patron:Service', 'Other']
    return order
      .filter((k) => groups.has(k))
      .map((k) => ({ key: k, label: groupLabel(k), forms: groups.get(k) }))
  }, [filteredForms])

  const onPick = (id) => {
    setRecordQ('')
    if (id) navigate(`/compliance-safe/forms/${id}/records`)
  }

  const toggleGroup = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const previewKeys = []
  for (const row of rows) {
    for (const p of row.preview || []) {
      if (!previewKeys.includes(p.label)) previewKeys.push(p.label)
    }
    if (previewKeys.length >= 3) break
  }

  const selectedForm = forms.find((f) => f.id === formId)

  return (
    <div className="p-6 min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Form records</h1>
          <p className="text-sm text-slate-500 mt-1">
            Pick a form from the list — submission counts show at a glance. Open a row for full answers.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/compliance-safe/forms')}>Forms</Button>
          <Button variant="outline" onClick={() => navigate('/compliance-safe/forms/pipeline')}>Pipeline</Button>
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-[minmax(260px,300px)_minmax(0,1fr)] lg:gap-5 lg:items-start">
        <aside className="mb-4 lg:mb-0 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-3 py-3 border-b border-slate-100">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Find form
            </label>
            <input
              className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm"
              placeholder="Name or record code…"
              value={formFilter}
              onChange={(e) => setFormFilter(e.target.value)}
            />
          </div>
          <div className="max-h-[min(70vh,640px)] overflow-y-auto">
            {formsLoading ? (
              <div className="px-3 py-4 text-xs text-slate-500">Loading forms…</div>
            ) : null}
            {!formsLoading && filteredForms.length === 0 ? (
              <div className="px-3 py-4 text-xs text-slate-500">No forms match your search.</div>
            ) : null}
            {groupedForms.map((group) => {
              const isOpen = collapsed[group.key] !== true
              return (
                <div key={group.key} className="border-b border-slate-100 last:border-0">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left bg-slate-50/80 hover:bg-slate-100/80"
                  >
                    {isOpen ? (
                      <ChevronDownIcon className="h-4 w-4 text-slate-400 shrink-0" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4 text-slate-400 shrink-0" />
                    )}
                    <span className="text-[11px] font-bold uppercase tracking-wide text-slate-600 flex-1">
                      {group.label}
                    </span>
                    <span className="text-[10px] font-semibold text-slate-400">{group.forms.length}</span>
                  </button>
                  {isOpen ? (
                    <ul className="py-1">
                      {group.forms.map((f) => {
                        const active = f.id === formId
                        const count = f.submissionCount || 0
                        return (
                          <li key={f.id}>
                            <button
                              type="button"
                              onClick={() => onPick(f.id)}
                              className={`w-full text-left px-3 py-2.5 border-l-2 transition-colors ${
                                active
                                  ? 'border-teal-600 bg-teal-50/60'
                                  : 'border-transparent hover:bg-slate-50'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-slate-600">
                                  {formRecordCode(f)}
                                </span>
                                <span
                                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                    count > 0 ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-500'
                                  }`}
                                >
                                  {count}
                                </span>
                              </div>
                              <div className={`text-sm font-medium mt-1 ${active ? 'text-teal-900' : 'text-slate-800'}`}>
                                {f.name}
                                {f.status === 'archived' ? (
                                  <span className="ml-1 text-[10px] font-semibold uppercase text-slate-400">Archived</span>
                                ) : null}
                              </div>
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                {f.facing === 'patron' ? 'Patron' : 'Internal'}
                                {f.status === 'published' ? ' · Published' : ` · ${f.status}`}
                              </div>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  ) : null}
                </div>
              )
            })}
          </div>
        </aside>

        <div className="min-w-0">
          {selectedForm ? (
            <div className="mb-3">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">
                  {formRecordCode(selectedForm)}
                </span>
                <h2 className="text-lg font-semibold text-slate-900 m-0">{selectedForm.name}</h2>
              </div>
              <p className="text-sm text-slate-500">
                {selectedForm.facing === 'patron' ? 'Patron send' : 'Internal assign'}
                {form?.currentVersionNo ? ` · published v${form.currentVersionNo}` : ''}
                {` · ${rows.length} record${rows.length === 1 ? '' : 's'}`}
              </p>
            </div>
          ) : null}

          <label className="block mb-4 max-w-md">
            <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Search records
            </span>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Ref, patron, or submitter"
              value={recordQ}
              onChange={(e) => setRecordQ(e.target.value)}
              disabled={!formId}
            />
          </label>

          {error ? (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}
          {loading ? <div className="text-sm text-slate-500">Loading records…</div> : null}

          {!formId && !loading ? (
            <div className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center">
              Select a form from the list to view submitted records.
            </div>
          ) : null}

          {formId && !loading && !rows.length && !error ? (
            <div className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center">
              No submitted records for this form yet.
            </div>
          ) : null}

          {rows.length ? (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-2.5 font-semibold">Ref</th>
                    <th className="px-4 py-2.5 font-semibold">Submitted</th>
                    <th className="px-4 py-2.5 font-semibold">Subject</th>
                    <th className="px-4 py-2.5 font-semibold">By</th>
                    <th className="px-4 py-2.5 font-semibold">Status</th>
                    {previewKeys.map((k) => (
                      <th key={k} className="px-4 py-2.5 font-semibold">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const previewMap = Object.fromEntries((r.preview || []).map((p) => [p.label, p.value]))
                    return (
                      <tr
                        key={r.id}
                        className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/compliance-safe/forms/assignments/${r.assignmentId}`)}
                      >
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-800">{r.ref}</td>
                        <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtWhen(r.submittedAt)}</td>
                        <td className="px-4 py-2.5 text-slate-800">
                          {r.subjectLabel || r.subjectType || '—'}
                          {r.isTest ? (
                            <span className="ml-1 text-[10px] font-semibold uppercase text-amber-700">Test</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">
                          {r.submittedByName || (r.facing === 'patron' ? (r.subjectLabel || 'Patron') : '—')}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-700">
                            {r.status}
                          </span>
                        </td>
                        {previewKeys.map((k) => (
                          <td key={k} className="px-4 py-2.5 text-slate-600 max-w-[180px] truncate">
                            {previewMap[k] || '—'}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
